import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { compressAudio, getAudioPublicUrl, prefetchAudioUrls } from "./audioUtils";
import BottomNav from "./BottomNav";
import { PopMenu, TopBar, anchorOf } from "./Chrome";
import { LangIntroModal, LangPicker } from "./components/Lang";
import { UndoToast } from "./components/UndoToast";
import { reportError } from "./errorLog";
import { LANG, t } from "./i18n";
import Icon from "./Icon";
import { lazyWithReload } from "./lazyWithReload";
import { isAdmin as checkIsAdmin, getAnnouncement, getProfile, myStatus, touchProfile } from "./library";
import { minimizeApp as nativeMinimize, onDeepLink } from "./nativeBridge";
import { enqueue, flushSoon, outboxSize } from "./outbox";
import { addToQueue, playNextInQueue, removeFromQueue, takeNext, useQueue } from "./queue";
import QueueSheet from "./QueueSheet";
import { AuthScreen } from "./screens/Auth";
import FsrsSettings from "./screens/FsrsSettings";
import { ProjectsScreen } from "./screens/Home";
import { MiniPlayer } from "./screens/MiniPlayer";
import { Player, withFiniteDuration } from "./screens/player/Player";
import { QuizScreen } from "./screens/Quiz";
import { AudioScreen, ReadingScreen, ReviewScreen } from "./screens/Sections";
import { AccountModal, NewPasswordModal } from "./screens/Settings";
import { StatsScreen } from "./screens/Stats";
import { UploadScreen } from "./screens/Upload";
import { markSetting } from "./settings";
import SideNav from "./SideNav";
import { todayPlan } from "./srs";
import { checkAudioDownloaded, checkStorageBeforeDownload, deleteLocalFolder, deleteNativeAudio, deleteProjectLocal, deleteWebAudio, downloadAudioWithProgress, downloadWebAudio, ensureAudioDownloaded, getPlayableAudioUri, getWebAudioCacheUrl, isOnline, loadLocalFolders, loadLocalProjects, saveLocalFolder, saveProjectLocal, srsRow, syncDailyStats, syncWebAudioStatus, syncWithSupabase } from "./storage";
import { getSessionOfflineSafe, readStoredSession, signOutNow, supabase } from "./supabase";
import { createTextProject, deleteTextProject, loadLocalTextProjects, loadTextProjects, upsertTextProject } from "./textProjects";
import { FONT, THEMES, THEME_META, applyThemeToDocument, isDarkTheme, isProTheme, nextTheme, shownTheme, themeKit } from "./themes";
import { setTableScene, startArcadeTable, stopArcadeTable } from "./arcadeTable";
import { startArcadeJuice, stopArcadeJuice } from "./arcadeJuice";
import { startTableSounds, stopTableSounds } from "./tableSounds";
import { ThemeContext } from "./ui/theme";
import { PullIndicator, usePullToRefresh } from "./app/PullToRefresh";
import useBgMediaSession from "./app/useBgMediaSession";
import useClearStats from "./app/useClearStats";
import useSettingsSync from "./app/useSettingsSync";
import { markSynced, useManualSync, useSyncStatus } from "./app/useSync";
import useUndoDeletes from "./app/useUndoDeletes";
import Toaster, { notify } from "./ui/Toaster";
import useConfirm from "./ui/useConfirm";
import { closeTopOnBack } from "./ui/useBackClose";
import { detectTextLangByScript, getActiveLang, getSavedTheme, isElectron, isNative, saveActiveLang, saveTheme } from "./utils";
import { WordLearningProvider, useWordActions } from "./wordLearning";

const ReaderScreen = lazyWithReload(() => import("./TextReader"));

const ImportBook = lazyWithReload(() => import("./ImportBook"));
const AdminPanel = lazyWithReload(() => import("./AdminPanel"));
const UiGallery = lazyWithReload(() => import("./ui/Gallery"));
const GamesScreen = lazyWithReload(() => import("./games/GamesScreen"));

// Electron overlay bridge: слушает сохранения слов из плавающей карточки
function OverlayBridge() {
  const { saveWord } = useWordActions() || {};
  useEffect(() => {
    if (!window.electronAPI || !saveWord) return;
    const handler = (e) => {
      const { word, status, translation, notes, wordLang } = e.detail || {};
      if (word) saveWord({ word, status: Number(status), translation: translation || '', notes: notes || '', wordLang: wordLang || '' });
    };
    window.addEventListener('overlay_save_word', handler);
    return () => window.removeEventListener('overlay_save_word', handler);
  }, [saveWord]);
  return null;
}

// Причина неудачной синхронизации — по-человечески.
function syncReasonText(reason){
  const r=String(reason||"");
  if(r==="offline")return t("нет интернета");
  if(r==="timeout")return t("сервер не ответил за 15 секунд");
  if(r==="no_session"||r==="refresh_failed")return t("вход устарел — выйдите и войдите снова");
  if(/fetch|network|load failed/i.test(r))return t("нет связи с сервером");
  return r;
}

// ── App — корневой компонент ──────────────────────────────────────────────────
// Управляет сессией, экранами, проектами, аудио, загрузкой, синхронизацией.

// Поля карточки, изменение которых нужно отправить на сервер.
const CARD_FIELDS=["time_sec","word","translation","color","srs_state","srs_interval","srs_ease","srs_due","srs_stability","srs_difficulty","srs_reps","srs_lapses","srs_last_review"];

// Кэш в памяти: мгновенное восстановление при возврате из плеера
let _memProjCache = { userId: null, projects: null };

// ── Routing ──────────────────────────────────────────────────────────────────
// URL is the source of truth. Every primary screen has its own hash; navigation
// uses pushState so the browser back/forward arrows just work; popstate re-reads
// the hash and updates the React `screen` state. Refresh on any screen restores
// where the user was.
//
// On the projects screen we encode the filter state (active language, open
// folder, search query) as query params after the hash, e.g.
//   #/projects?lang=ja&folder=News&q=hello
// Each transition (language change / folder open / search activation) becomes
// a real history entry so browser Back/Forward navigate naturally between them.
//
//   #/projects              — library (default)
//   #/projects?lang=ja      — library filtered to Japanese
//   #/projects?folder=News  — folder open
//   #/projects?q=foo        — search active
//   #/player/<projectId>    — audio player for a specific project
//   #/upload                — upload wizard (needs activeProj — falls back if cold)
//   #/quiz                  — SRS / quiz
//   #/reader/<projectId>    — text reader for a specific text project
//   #/stats                 — stats screen
//   #/vocab_quiz            — vocab quiz (needs in-memory words — falls back if cold)
//   #/auth                  — auth screen
// ── История браузера ──────────────────────────────────────────────────────────
// Каждая запись хранит свою глубину idx. Стрелка «←» на экранах с фокусом делает
// шаг назад, только если прошлый экран был внутри приложения (idx>0); иначе —
// открывает родительский раздел. В историю пишутся только настоящие переходы
// (раздел, папка, проект); язык, поиск и всплывающие окна её не засоряют.
const NAV_SCREENS=new Set(["projects","audio","review","reading","stats"]);
// Какой цвет стола «Аркады» у экрана (THEMES.arcade.scenes); остальные — цвет главной.
const TABLE_SCENE={projects:"home",quiz:"review",review:"review",games:"review",audio:"audio",player:"audio",upload:"audio",reading:"reading",reader:"reading",stats:"stats"};
const NAV_ACTIVE={projects:"home",audio:"audio",review:"review",games:"review",reading:"read",stats:"stats"};
const NAV_TITLE={projects:t("Главная"),audio:t("Аудио"),review:t("Повторение"),reading:t("Чтение"),stats:t("Статистика")};
const histIdx=()=>{try{return window.history.state?.idx||0;}catch{return 0;}};
const histPush=(state,url)=>{try{window.history.pushState({...state,idx:histIdx()+1},"",url);}catch{}};
const histReplace=(url,state)=>{try{window.history.replaceState({...(window.history.state||{}),...(state||{}),idx:histIdx()},"",url);}catch{}};

const buildHash = (scr, projId, opts = {}) => {
  let base;
  if (scr === "player" && projId)       base = `#/player/${projId}`;
  else if (scr === "reader" && projId)  base = `#/reader/${projId}`;
  else if (scr === "reader")            base = "#/reader";
  else if (scr === "quiz")              base = "#/quiz";
  else if (scr === "upload")            base = "#/upload";
  else if (scr === "stats")             base = "#/stats";
  else if (scr === "review")            base = "#/review";
  else if (scr === "games")             base = projId === "leeches" ? "#/games/leeches" : "#/games";
  else if (scr === "reading")           base = "#/reading";
  else if (scr === "audio")             base = "#/audio";
  else if (scr === "vocab_quiz")        base = "#/vocab_quiz";
  else if (scr === "auth")              base = "#/auth";
  else if (scr === "projects")          base = "#/projects";
  else return "";
  if (scr === "projects") {
    const parts = [];
    if (opts.lang)   parts.push("lang="   + encodeURIComponent(opts.lang));
    if (opts.folder) parts.push("folder=" + encodeURIComponent(opts.folder));
    if (opts.search) parts.push("q="      + encodeURIComponent(opts.search));
    if (parts.length) base += "?" + parts.join("&");
  }
  return base;
};
const parseRoute = (hash) => {
  const empty = { scr: "projects", id: null, lang: null, folder: null, search: null };
  if (!hash || hash === "#" || hash === "#/") return empty;
  const qIdx = hash.indexOf("?");
  const path = qIdx >= 0 ? hash.slice(0, qIdx) : hash;
  const query = qIdx >= 0 ? hash.slice(qIdx + 1) : "";
  const params = new URLSearchParams(query);
  const m = path.match(/^#\/([^/]+)(?:\/(.+))?$/);
  if (!m) return empty;
  const scr = m[1];
  const id  = m[2] || null;
  // Map "player" without id back to projects so the player never mounts orphaned.
  if (scr === "player" && !id) return empty;
  return {
    scr,
    id,
    lang:   params.get("lang")   || null,
    folder: params.get("folder") || null,
    search: params.get("q")      || null,
  };
};

export default function App() {
  const [session,setSession]=useState(null);
  // Тот же человек — тот же объект: обновление токена (раз в час) и повторные события
  // входа больше не перезапускают загрузку всех данных (раньше на старте по 2–3 раза
  // тянулись проекты, тексты и языки). Токен приложению не нужен — его держит клиент Supabase.
  const setSessionStable=useCallback((next)=>{
    setSession(prev=>{
      if(prev&&next&&prev.user?.id===next.user?.id&&prev.user?.email===next.user?.email)return prev;
      return next||null;
    });
  },[]);
  const [screen,setScreen]=useState("loading");
  const [projects,setProjects]=useState([]);
  const [extraFolders,setExtraFolders]=useState([]);
  const [activeProj,setActiveProj]=useState(null);
  const [audioSrc,setAudioSrc]=useState(null);
  const [audioOwnerId,setAudioOwnerId]=useState(null); // id проекта которому принадлежит текущее аудио
  // ── Фоновое аудио (играет пока смотришь другой проект) ──────────────────
  const bgAudioRef=useRef(null);
  const [bgProj,setBgProj]=useState(null);
  const [bgState,setBgState]=useState({playing:false,cur:0,dur:0,vol:1});
  const [bgMiniClosed,setBgMiniClosed]=useState(false);
  const [uploading,setUploading]=useState(false);
  const [compressProgress,setCompressProgress]=useState(null); // null=неактивно, 0-100=сжатие
  const [projLoading,setProjLoading]=useState(true); // true: пока проекты не прочитаны — заготовки карточек, а не «Добро пожаловать»
  // «Синхронизация…» и её итог в шапке (app/useSync).
  const syncStatus=useSyncStatus();
  const {busy:syncing,result:syncResult,detail:syncErrorDetail,start:syncStart,finish:syncFinish}=syncStatus;
  const [pendingFolder,setPendingFolder]=useState(null);
  const [quizProjId,setQuizProjId]=useState(undefined);
  const [quizAutoSrs,setQuizAutoSrs]=useState(false);
  // Игры: только пиявки (#/games/leeches) или все карточки (#/games).
  const [gamesLeech,setGamesLeech]=useState(false);
  // Тема: выбранная хранится как есть, а показывается shownTheme — темы Pro («Колода»,
  // «Аркада») без тарифа Pro (или админа) не видны. isPro: true | false | null (ещё не знаем);
  // последнее известное — в ac_pro, чтобы без сети не мигало.
  const [themePref,setTheme]=useState(getSavedTheme);
  const [isPro,setIsPro]=useState(()=>{try{const v=localStorage.getItem("ac_pro");return v==="1"?true:v==="0"?false:null;}catch{return null;}});
  const [userName,setUserName]=useState("");
  const theme=shownTheme(themePref,isPro);
  // Активный язык (LingQ-style). Все новые проекты + сбор статы по дефолту попадают в него.
  // Filter state for the projects screen. URL is the source of truth — these
  // states mirror ?lang=&folder=&q= in the hash so browser Back/Forward navigate
  // between filter changes naturally. Initial values come from URL if present
  // (deep-link / refresh), otherwise from localStorage / empty defaults.
  const _initialRoute = (()=>{try{return parseRoute(window.location.hash);}catch{return {scr:"projects",lang:null,folder:null,search:null};}})();
  const [activeLang,setActiveLang]=useState(()=>{
    if(_initialRoute.scr==="projects"&&_initialRoute.lang!==null) return (_initialRoute.lang||'').toLowerCase();
    return getActiveLang();
  });
  const [activeFolder,setActiveFolder]=useState(()=>{
    if(_initialRoute.scr==="projects"&&_initialRoute.folder) return _initialRoute.folder;
    try{return localStorage.getItem("ac_active_folder")||null;}catch{return null;}
  });
  const [searchQuery,setSearchQuery]=useState(()=>{
    if(_initialRoute.scr==="projects"&&_initialRoute.search) return _initialRoute.search;
    return "";
  });
  // Persist folder selection across sessions (URL takes precedence on cold load).
  useEffect(()=>{try{if(activeFolder)localStorage.setItem("ac_active_folder",activeFolder);else localStorage.removeItem("ac_active_folder");}catch{}},[activeFolder]);
  // Refs mirror the latest values for use inside callbacks without re-creating them.
  const activeLangRef=useRef(activeLang);
  useEffect(()=>{activeLangRef.current=activeLang;},[activeLang]);
  const activeFolderRef=useRef(activeFolder);
  useEffect(()=>{activeFolderRef.current=activeFolder;},[activeFolder]);
  const searchQueryRef=useRef(searchQuery);
  useEffect(()=>{searchQueryRef.current=searchQuery;},[searchQuery]);
  // ── Projects-screen URL helper ────────────────────────────────────────────
  // Single entry point for changing filter state. Writes both React state and
  // the URL hash atomically. Use mode="push" to create a back-navigable history
  // entry, "replace" to update in place (search keystrokes, init reconcile).
  const navProjects=useCallback((updates={},mode="push")=>{
    const nextLang   = updates.lang   !== undefined ? updates.lang   : activeLangRef.current;
    const nextFolder = updates.folder !== undefined ? updates.folder : activeFolderRef.current;
    const nextSearch = updates.search !== undefined ? updates.search : searchQueryRef.current;
    const normLang   = (nextLang||"").toLowerCase();
    if(normLang !== activeLangRef.current){ saveActiveLang(normLang); setActiveLang(normLang); }
    if((nextFolder||null) !== (activeFolderRef.current||null)) setActiveFolder(nextFolder||null);
    if((nextSearch||"")   !== (searchQueryRef.current||""))   setSearchQuery(nextSearch||"");
    const target = buildHash("projects",null,{
      lang:   normLang   || null,
      folder: nextFolder || null,
      search: nextSearch || null,
    });
    if(window.location.hash !== target){
      try{
        if(mode==="replace") histReplace(target,{scr:"projects"});
        else                 histPush({scr:"projects"},target);
      }catch{}
    }
  },[]);
  // Язык — настройка, а не переход: в историю не пишем.
  const applyActiveLang=useCallback((l)=>{
    const norm=(l||"").toLowerCase();
    if(parseRoute(window.location.hash).scr!=="projects"){
      if(norm!==activeLangRef.current){saveActiveLang(norm);setActiveLang(norm);}
      return;
    }
    navProjects({ lang: norm || null }, "replace");
  },[navProjects]);
  const setActiveFolderUrl=useCallback((f)=>{ navProjects({ folder: f || null }); },[navProjects]);
  // Search: push a new history entry when search becomes active (empty→text),
  // replace on subsequent keystrokes so we don't spam history. Clearing also
  // pushes so back from cleared restores the last query and forward re-clears.
  const setSearchQueryUrl=useCallback((q)=>{
    // Поиск тоже не переход: набор и очистка меняют адрес на месте.
    navProjects({ search: q || null }, "replace");
  },[navProjects]);
  // Auto-cleanup of stale folder selection — replace mode so we don't add a
  // back-navigable phantom history entry. (Used to live in ProjectsScreen,
  // but it needs access to the App-level URL helper.)
  useEffect(()=>{
    if(!activeFolder) return;
    if(!projects || projects.length===0) return;
    const allFolders=new Set([
      ...projects.map(p=>p.folder).filter(Boolean),
      ...(extraFolders||[]),
    ]);
    if(!allFolders.has(activeFolder)) navProjects({folder:null},"replace");
  },[activeFolder,projects,extraFolders,navProjects]);
  // Выбор языка — только новому пользователю (см. эффект ниже, после textProjects).
  const [showLangIntro,setShowLangIntro]=useState(false);
  // Настройки и свои языки между устройствами; пришли новые — перечитываем тему, язык, папки.
  const settingsPulled=useSettingsSync(session,(keys,uid)=>{
    if(keys.includes('ac_theme')){ try{setTheme(getSavedTheme());}catch{} }
    if(keys.includes('ac_active_lang')){ try{setActiveLang(getActiveLang());}catch{} }
    if(keys.some(k=>k.startsWith('ac_folders_'))){ try{setExtraFolders(loadLocalFolders(uid));}catch{} }
  });
  const closeLangIntro=useCallback((picked)=>{
    if(picked)applyActiveLang(picked);
    try{localStorage.setItem("ac_lang_intro_seen","1");markSetting("ac_lang_intro_seen");}catch{}
    setShowLangIntro(false);
  },[applyActiveLang]);
  const [dlStatus,setDlStatus]=useState(()=>{
    // Init from web cache status for non-native (оптимистично показываем "done")
    try { const st=JSON.parse(localStorage.getItem("ac_offline_audio")||"{}"); return Object.fromEntries(Object.keys(st).map(id=>[id,"done"])); } catch { return {}; }
  });
  const [textProjects,setTextProjects]=useState([]);
  // «Какой язык учишь?» — только совсем новому пользователю. Решаем, когда
  // пришли настройки с сервера (отметка «уже спрашивали» синхронизируется между
  // устройствами) и прочитаны проекты: у кого уже есть аудио или тексты, того
  // не спрашиваем — просто запоминаем, что вопрос не нужен.
  useEffect(()=>{
    if(!session||!settingsPulled||projLoading)return;
    let seen=false;
    try{seen=localStorage.getItem("ac_lang_intro_seen")==="1";}catch{}
    if(seen||getActiveLang())return;
    if(projects.length||textProjects.length){
      try{localStorage.setItem("ac_lang_intro_seen","1");markSetting("ac_lang_intro_seen");}catch{}
      return;
    }
    setShowLangIntro(true);
  },[session,settingsPulled,projLoading,projects.length,textProjects.length]);
  const [activeTextProj,setActiveTextProj]=useState(null);
  const pendingSeekRef=useRef(null);
  const [playerSeek,setPlayerSeek]=useState(null);

  // ── Синхронизация статусов: если кэш браузера очищен — сбрасываем галочки ──
  useEffect(()=>{
    if(!projects.length)return;
    const ids=projects.map(p=>p.id);
    syncWebAudioStatus(ids).then(synced=>{
      if(!Object.keys(synced).length)return;
      setDlStatus(prev=>{
        const next={...prev};
        Object.entries(synced).forEach(([id,st])=>{
          if(st==="idle"&&next[id]==="done")next[id]="idle";
          else if(st==="done"&&!next[id])next[id]="done";
        });
        return next;
      });
    }).catch(()=>{});
  },[projects]);

  const applyTheme=useCallback((k)=>{
    if(isProTheme(k)&&isPro!==true){notify(t("Тема «{0}» — для Pro",[t(THEME_META[k].name)]));return;}
    saveTheme(k);setTheme(k);
  },[isPro]);
  // Тема на уровне страницы (шрифт, атрибут data-theme, цвет строки состояния) и полоса окна
  // программы для компьютера: она знает только светлую и тёмную, остальные — по признаку dark.
  useEffect(()=>{applyThemeToDocument(theme);try{window.electronAPI?.setAppInfo?.({theme:isDarkTheme(theme)?"dark":"light",lang:LANG});}catch{}},[theme]);
  // Живой стол и игровой отклик (искры, звуки, наклон) — по набору темы (themeKit: background, sounds);
  // звуки «Колоды» — записи фишек и карт (tableSounds).
  useEffect(()=>{const k=themeKit(THEMES[theme]);if(k.background==="table")startArcadeTable(THEMES[theme].table);else stopArcadeTable();
    if(k.sounds==="arcade")startArcadeJuice();else stopArcadeJuice();if(k.sounds==="table")startTableSounds();else stopTableSounds();},[theme]);
  // Стол меняет цвета по разделам (THEMES.arcade.scenes): главная, повторение, аудио, чтение, статистика.
  useEffect(()=>{const sc=THEMES[theme]?.scenes;if(sc)setTableScene(sc[TABLE_SCENE[screen]]||THEMES[theme].table);},[theme,screen]);

  // Forward navigation: pushState builds a real browser history entry so the
  // back/forward arrows work natively, and refresh restores the screen from URL.
  // pushState is skipped when the target hash matches the current one (no-op
  // double-clicks, popstate-triggered renders).
  //
  // When navigating TO the projects screen we preserve the active language in
  // the URL (it's a long-lived "setting") but clear folder/search (transient
  // view state). Explicit callers can override via the opts argument.
  const setScreenAndHash=useCallback((scr,projId,opts,mode="push")=>{
    let finalOpts=opts||{};
    if(scr==="projects"){
      finalOpts={
        lang:   activeLangRef.current || null,
        folder: null,
        search: null,
        ...opts,
      };
      // Sync state so it matches the URL we're about to push.
      if((finalOpts.folder||null) !== (activeFolderRef.current||null)) setActiveFolder(finalOpts.folder||null);
      if((finalOpts.search||"")   !== (searchQueryRef.current||""))    setSearchQuery(finalOpts.search||"");
    }
    const hash=buildHash(scr,projId,finalOpts);
    const target=hash||window.location.pathname;
    if(window.location.hash!==hash){
      if(mode==="replace")histReplace(target,{scr,projId:projId||null});
      else histPush({scr,projId:projId||null,...finalOpts},target);
    }
    setScreen(scr);
  },[]);

  // Проверка хранилища браузера

  useEffect(()=>{
    // Отменяемся при размонтировании: иначе повторный запуск эффекта (StrictMode)
    // возвращал экран проектов уже после того, как открылся плеер по ссылке.
    let cancelled=false;
    const route=(sess)=>{
      if(cancelled)return;
      setSessionStable(sess);
      const hash=window.location.hash;
      if(!sess){setScreen("auth");return;}
      const {scr,id}=parseRoute(hash);
      // Helper: when landing on projects, make the URL canonical w.r.t. current
      // filter state (e.g. `#/projects` → `#/projects?lang=ja`). Otherwise the
      // first user-initiated change pushes a richer URL, and pressing Back
      // would land on a state-less URL that wipes the language filter.
      const reconcileProjectsUrl=()=>{
        try{
          const target=buildHash("projects",null,{
            lang:   activeLangRef.current   || null,
            folder: activeFolderRef.current || null,
            search: searchQueryRef.current  || null,
          });
          if(window.location.hash!==target) histReplace(target);
        }catch{}
      };
      // Player: defer to loadProjects (it needs to find the project + load audio).
      if(scr==="player"&&id){
        try{localStorage.setItem("ac_pending_player",id);}catch{}
        setScreen("projects");return;
      }
      // Quiz: restore in-progress SRS session if stashed.
      if(scr==="quiz"){
        try{
          const nav=JSON.parse(sessionStorage.getItem("ac_quiz_nav")||"null");
          if(nav){setQuizProjId(nav.projId);setQuizAutoSrs(!!nav.autoSrs);}
        }catch{}
        setScreen("quiz");return;
      }
      // Reader: show projects skeleton until loadTextProjects hydrates the right
      // text project (selected by id from URL, falling back to localStorage).
      if(scr==="reader"){setScreen("projects");return;}
      // Stats: standalone screen, no extra state to hydrate.
      if(scr==="stats"){setScreen("stats");return;}
      if(scr==="games"){setGamesLeech(id==="leeches");setScreen("games");return;}
      // Витрина деталей интерфейса (src/ui/Gallery.js) — служебная, для автопроверки.
      if(scr==="ui"){setScreen("ui");return;}
      if(scr==="review"||scr==="reading"||scr==="audio"){setScreen(scr);return;}
      // Upload / vocab_quiz: depend on in-memory state (activeProj / vocab words)
      // that can't be recovered from URL alone. Clean the hash and land on home.
      if(scr==="upload"||scr==="vocab_quiz"){
        histReplace(window.location.pathname+"#/projects");
        setScreen("projects");
        reconcileProjectsUrl();
        return;
      }
      try{localStorage.removeItem("ac_pending_player");}catch{}
      setScreen("projects");
      reconcileProjectsUrl();
    };
    // Есть сохранённый вход — экран открывается сразу по данным устройства, а вход
    // проверяется на сервере в фоне. Если он недействителен (сменили пароль и т.п.),
    // Supabase пришлёт SIGNED_OUT — и обработчик ниже откроет экран входа.
    const stored=readStoredSession();
    if(stored){
      route(stored);
      getSessionOfflineSafe().then(s=>{if(!cancelled&&s)setSessionStable(s);});
    }else getSessionOfflineSafe().then(route);
    const{data:{subscription}}=supabase.auth.onAuthStateChange((event,sess)=>{
      setSessionStable(sess);
      if(event==="PASSWORD_RECOVERY")setRecovery(true);
      if((event==="SIGNED_IN")&&sess){
        // Only handle the *new sign-in* case (user came from the auth form).
        // We must NOT touch scr==="loading" — on a page refresh with a valid
        // stored session, SIGNED_IN can fire BEFORE the init effect's hash
        // parser resolves. Forcing screen to "projects" + replacing URL with
        // "#/projects" would wipe the user's deep-link (e.g. refresh on
        // #/stats → kicked back to projects). When loading, just let the init
        // effect set the screen from the hash.
        setScreen(scr=>{
          if(scr==="auth"){
            try{
              const target=buildHash("projects",null,{lang:activeLangRef.current||null});
              if(window.location.hash!==target)histReplace(target);
            }catch{}
            return"projects";
          }
          return scr;
        });
      }
      if(event==="TOKEN_REFRESHED"&&sess){/* keep current screen */}
      if(event==="SIGNED_OUT")setScreenAndHash("auth");
    });
    return()=>{cancelled=true;subscription.unsubscribe();};
  },[]);

  const didInitNav=useRef(false);
  const prevScreenRef=useRef(null);
  const firstSyncDoneRef=useRef(false);

  const loadProjects=useCallback(async(navAfter=false)=>{
    if(!session)return;
    setProjLoading(true);
    const userId=session.user.id;
    setExtraFolders(loadLocalFolders(userId));

    // ── Тексты: сразу из локальной копии, свежая версия с сервера — в фоне ──
    // (раньше тексты и переход по ссылке #/reader ждали окончания синхронизации, до 15 с)
    let readerResolved=false;
    const applyTexts=(tp,final)=>{
      setTextProjects(tp||[]);
      const hash=window.location.hash;
      if(!readerResolved&&hash.startsWith("#/reader")){
        try{
          // Prefer the id from the URL (cold-refresh deterministic restore);
          // fall back to localStorage for legacy `#/reader` without id.
          const m=hash.match(/^#\/reader\/(.+)$/);
          let savedId=m?decodeURIComponent(m[1]):null;
          if(!savedId)savedId=localStorage.getItem("ac_reader_proj");
          const rp=(tp||[]).find(p=>p.id===savedId);
          if(rp){
            readerResolved=true;
            setActiveTextProj(rp);
            // Keep URL in sync with the resolved id so future refreshes stay deterministic.
            if(!m)histReplace(`#/reader/${rp.id}`);
            setScreen("reader");
          }else if(final){
            // Stale id — clean the URL so user lands cleanly on /projects (preserving language filter).
            try{
              const target=buildHash("projects",null,{lang:activeLangRef.current||null});
              histReplace(target);
            }catch{}
          }
        }catch{}
      }
      // Авто-детект языка — в фоне, не блокируя рендер (regex по началу текста — миллисекунды).
      setTimeout(()=>{
        let detected;
        try{detected=JSON.parse(localStorage.getItem("ac_lang_autodetect_done")||"{}");}catch{detected={};}
        const updates=[];
        (tp||[]).forEach(p=>{
          const cur=(p.src_lang||'').toLowerCase();
          if((cur&&cur!=='en')||detected[p.id])return;
          if(!p.content)return;
          const guess=detectTextLangByScript(p.content);
          if(guess&&guess!==cur)updates.push({...p,src_lang:guess});
          detected[p.id]=1;
        });
        try{localStorage.setItem("ac_lang_autodetect_done",JSON.stringify(detected));}catch{}
        if(updates.length){
          const byId=new Map(updates.map(u=>[u.id,u]));
          setTextProjects(prev=>prev.map(p=>byId.get(p.id)||p));
          updates.forEach(u=>{upsertTextProject(userId,u).catch(()=>{});});
        }
      },0);
    };
    applyTexts(loadLocalTextProjects(userId),false);
    loadTextProjects(userId).then(tp=>applyTexts(tp,true)).catch(()=>{});

    // Мгновенный показ из памяти (возврат из плеера)
    if(_memProjCache.userId===userId&&_memProjCache.projects?.length>0){
      setProjects(_memProjCache.projects);
      setProjLoading(false);
      prefetchAudioUrls(supabase,_memProjCache.projects);
    }

    const localRaw=await loadLocalProjects(userId);
    // Recompute cardCount from actual cards (prevents stale counts from cache)
    const local=localRaw.map(p=>({...p,cardCount:(p.cards||[]).length}));
    // Обновляем кэш в памяти
    _memProjCache={userId,projects:local};
    // Если есть pending player — не показываем projects screen, ждём навигации
    const hasPending=(()=>{try{return!!localStorage.getItem("ac_pending_player");}catch{return false;}})();
    if(local.length>0&&!hasPending){setProjects(local);setProjLoading(false);prefetchAudioUrls(supabase,local);}

    // Проверяем скачанные файлы
    if(local.length>0){
      const map={};
      await Promise.all(local.filter(p=>p.audio_path).map(async p=>{const f=await checkAudioDownloaded(p.id,p.audio_path);if(f)map[p.id]="done";}));
      if(Object.keys(map).length>0)setDlStatus(prev=>({...prev,...map}));
    }

    // Ссылка на плеер (#/player/<id>) — открываем сразу по локальным данным,
    // синхронизация идёт следом в фоне (раньше плеер ждал её до 15 с на экране «Загрузка…»).
    // На новом устройстве проекта ещё нет на месте — тогда откроем после синхронизации.
    const openLinkedPlayer=async(proj)=>{
      const lu=await getPlayableAudioUri(proj.local_audio_filename);
      const src=lu||getWebAudioCacheUrl(proj.id)||((await isOnline())&&proj.audio_path?getAudioPublicUrl(supabase,proj.audio_path):null);
      if(!src)return false;
      setActiveProj(proj);setAudioSrc(src);setAudioOwnerId(proj.id);setScreen("player");
      return true;
    };
    let linkedPid=null;
    if(navAfter&&!didInitNav.current&&hasPending){
      didInitNav.current=true;
      linkedPid=(()=>{try{return localStorage.getItem("ac_pending_player");}catch{return null;}})();
      // Всегда удаляем — даже если не будем по нему переходить
      try{localStorage.removeItem("ac_pending_player");}catch{}
      setProjects(local);setProjLoading(false);prefetchAudioUrls(supabase,local);
      const proj=local.find(p=>p.id===linkedPid);
      if(proj&&await openLinkedPlayer(proj))linkedPid=null;
    }

    const online=await isOnline();
    // Синхронизация — чтобы на всех устройствах были свежие данные, а не при каждом
    // переходе между разделами: при запуске, затем не чаще раза в 5 минут (плюс при
    // возврате в приложение и кнопкой — см. эффект ниже). Изменения отправляются сами
    // через очередь сразу после правки.
    const _lastSync=(()=>{try{return parseInt(localStorage.getItem("ac_last_sync")||"0");}catch{return 0;}})();
    const shouldSync=!firstSyncDoneRef.current||Date.now()-_lastSync>5*60*1000;
    firstSyncDoneRef.current=true;
    if(online&&shouldSync){
      syncStart();
      // 15-second hard timeout — prevents sync from blocking the app on slow/broken networks
      const result=await Promise.race([
        syncWithSupabase(userId),
        new Promise(resolve=>setTimeout(()=>resolve({success:false,reason:"timeout"}),15000)),
      ]);
      if(result.success&&result.projects){_memProjCache={userId,projects:result.projects};setProjects(result.projects);prefetchAudioUrls(supabase,result.projects);}
      // Проект из ссылки пришёл только с сервера (новое устройство) — открываем, если человек
      // за время синхронизации никуда не ушёл.
      const linked=linkedPid&&result.projects?.find(p=>p.id===linkedPid);
      if(linked&&window.location.hash.startsWith(`#/player/${linkedPid}`))await openLinkedPlayer(linked);
      // ── Per-language daily stats sync ────────────────────────────────────
      // Pushes local stat deltas and pulls authoritative server state for
      // listen/read minutes, words read, cards reviewed. Requires the
      // user_daily_stats schema migration to be in place — falls back to a
      // soft warning if the table/RPC isn't there yet so the rest of sync
      // keeps working.
      let statsRes = { ok: true };
      // Не дольше 20 с: без ответа сервера «Синхронизация…» раньше могла висеть бесконечно.
      try { statsRes = await Promise.race([syncDailyStats(userId), new Promise(r=>setTimeout(()=>r({ok:false,reason:'timeout'}),20000))]); }
      catch(e){ console.warn('[Sync] daily stats:', e); }

      // Итог — в шапке, как после кнопки: зелёный / жёлтый (нет таблиц) / красный.
      if(result.success){
        markSynced();
        const projectsOk=result.reason!=="no_tables";
        const statsOk=statsRes.ok||!statsRes.reason;
        syncFinish(projectsOk&&statsOk?"ok":"no_tables","",8000);
      }else syncFinish("error",syncReasonText(result.reason),8000);
    }
    setProjLoading(false);

    // ── PTR resume: восстанавливаем таймкод после pull-to-refresh в плеере ──
    try{
      const ptrRaw=localStorage.getItem("ac_ptr_resume");
      if(ptrRaw){
        localStorage.removeItem("ac_ptr_resume");
        const {projId,time}=JSON.parse(ptrRaw);
        const proj=local.find(p=>p.id===projId);
        if(proj&&time>0){
          setPlayerSeek(time);
          handleOpen(proj,time);
          return;
        }
      }
    }catch{}

    didInitNav.current=true;
  },[session,syncStart,syncFinish]);

  useEffect(()=>{
    // Always track current screen so we can detect actual navigation vs session refresh
    const fromScreen=prevScreenRef.current;
    prevScreenRef.current=screen;
    if(session&&(screen==="projects"||screen==="audio"||screen==="quiz"||screen==="review"||screen==="games"||screen==="reading"||screen==="stats")){
      // Force sync only when the screen actually changed (navigated here),
      // not when TOKEN_REFRESHED silently updates session on the same screen.
      loadProjects(fromScreen!==screen);
    }
  },[session,screen]);

  // ── Debounced auto-push of stat changes to Supabase ────────────────────────
  // Every recordTime/recordWordsRead/recordActivity dispatches ac:activity-changed.
  // We wait 12 sec after the LAST change before pushing — quizzes that fire 20
  // events in 30 sec produce ONE network call, not 20. Events with detail.source
  // === 'sync' come from syncDailyStats itself; ignoring them prevents the
  // listener from re-scheduling itself in an infinite loop.
  useEffect(()=>{
    const uid=session?.user?.id;
    if(!uid)return;
    let timer=null;let inFlight=false;
    const schedule=(ev)=>{
      if(ev?.detail?.source==='sync')return;
      if(timer)clearTimeout(timer);
      timer=setTimeout(async()=>{
        if(inFlight)return;
        if(typeof navigator!=='undefined'&&navigator.onLine===false)return;
        inFlight=true;
        try{await syncDailyStats(uid);}catch{}
        inFlight=false;
      },12000);
    };
    window.addEventListener('ac:activity-changed',schedule);
    return()=>{
      window.removeEventListener('ac:activity-changed',schedule);
      if(timer)clearTimeout(timer);
    };
  },[session]);

  // ── Очередь аудио (как в подкастах) ──────────────────────────────────────
  const queueRaw=useQueue();
  // Удалённые аудио в очереди не считаем (их выбросит takeNext при переходе).
  const queue=useMemo(()=>{if(!projects.length)return queueRaw;const ids=new Set(projects.map(p=>String(p.id)));return queueRaw.filter(id=>ids.has(id));},[queueRaw,projects]);
  const [queueOpen,setQueueOpen]=useState(false);
  const queueNextRef=useRef(null); // включить следующее из очереди (задаётся ниже)
  const bgEndedRef=useRef(null);

  // ── bgAudio event listeners ─────────────────────────────────────────────
  useEffect(()=>{
    const audio=bgAudioRef.current;
    if(!audio)return;
    const onMeta=()=>withFiniteDuration(audio,(d)=>setBgState(s=>({...s,dur:d})));
    const onPlay=()=>setBgState(s=>({...s,playing:true}));
    const onPause=()=>setBgState(s=>({...s,playing:false}));
    const onEnded=()=>{setBgState(s=>({...s,playing:false}));setBgProj(null);bgEndedRef.current?.();};
    audio.addEventListener("loadedmetadata",onMeta);
    audio.addEventListener("play",onPlay);
    audio.addEventListener("pause",onPause);
    audio.addEventListener("ended",onEnded);
    return()=>{
      audio.removeEventListener("loadedmetadata",onMeta);
      audio.removeEventListener("play",onPlay);
      audio.removeEventListener("pause",onPause);
      audio.removeEventListener("ended",onEnded);
    };
  },[]);

  // Уведомление на телефоне показывает фоновое аудио, пока оно играет.
  useBgMediaSession(bgAudioRef,bgProj);

  const stopBgAudio=()=>{
    const a=bgAudioRef.current;
    // Выгрузить звук: src="" браузер понимает как адрес страницы и пишет ошибку «формат не поддерживается».
    if(a){a.pause();a.removeAttribute("src");a.load();}
    setBgProj(null);setBgMiniClosed(false);
  };

  const handleUpload=async(file)=>{
    if(!session)return;

    // ── Шаг 1: Сжатие (только для WAV/AIFF/FLAC) ─────────────────────────
    let fileToUpload=file;
    const rawExt=file.name.split(".").pop().toLowerCase();
    const needsCompress=["wav","aif","aiff","flac","pcm","raw"].includes(rawExt);
    if(needsCompress){
      setCompressProgress(0);
      fileToUpload=await compressAudio(file,(pct)=>{
        if(pct===null){setCompressProgress(null);}
        else{setCompressProgress(pct);}
      });
      setCompressProgress(null);
    }

    // ── Шаг 2: Загрузка в Supabase ────────────────────────────────────────
    setUploading(true);
    const userId=session.user.id;
    const ext=fileToUpload.name.split(".").pop();
    const path=`${userId}/${Date.now()}.${ext}`;
    const{error:upErr}=await supabase.storage.from("audio-files").upload(path,fileToUpload,{upsert:false});
    if(upErr){notify(t("Ошибка загрузки: ")+upErr.message,{tone:"error"});setUploading(false);return;}

    const publicUrl=getAudioPublicUrl(supabase,path);
    if(activeProj){
      await supabase.from("projects").update({audio_path:path,updated_at:new Date().toISOString()}).eq("id",activeProj.id);
      const fn=await ensureAudioDownloaded(activeProj.id,publicUrl);
      const pu=fn?(await getPlayableAudioUri(fn)):publicUrl;
      setAudioSrc(pu||publicUrl);setAudioOwnerId(activeProj.id);setScreenAndHash("player",activeProj.id);
    }else{
      const name=fileToUpload.name.replace(/\.[^/.]+$/,"");
      const ins={user_id:userId,name,audio_path:path};
      if(pendingFolder)ins.folder=pendingFolder;
      // Активный язык как дефолт; пустой если не выбран — попадёт под мастер-миграцию.
      const al=getActiveLang();
      if(al)ins.src_lang=al;
      const{data:newProj}=await supabase.from("projects").insert(ins).select().single();
      if(newProj){
        const proj={...newProj,cards:[],cardCount:0};setActiveProj(proj);setProjects(p=>[proj,...p]);
        const fn=await ensureAudioDownloaded(newProj.id,publicUrl);
        const pu=fn?(await getPlayableAudioUri(fn)):publicUrl;
        setAudioSrc(pu||publicUrl);setAudioOwnerId(newProj.id);setScreenAndHash("player",newProj.id);setPendingFolder(null);
      }
    }
    setUploading(false);
  };

  const openFromUrlRef=useRef(null);
  const handleOpen=async(proj,seekTime=null)=>{
    if(proj.id===activeProj?.id){
      setScreenAndHash("player",proj.id);
      // Seek если передан таймкод (из поиска или PTR-восстановления)
      if(seekTime!=null&&seekTime>0){
        setTimeout(()=>playerState?.seek?.(seekTime),120);
      }
      return;
    }
    setActiveProj(proj);setScreenAndHash("player",proj.id);
    if(seekTime!=null)pendingSeekRef.current={projId:proj.id,time:seekTime};
    if(playerState?.playing){
      // Перекидываем текущее аудио в фоновый плеер
      const bg=bgAudioRef.current;
      if(bg&&audioSrc){
        bg.src=audioSrc;
        bg.currentTime=playerState.audio?.currentTime||0;
        bg.volume=playerState.vol||1;
        bg.play().catch(()=>{});
        setBgProj(activeProj);
        setBgState({playing:true,cur:playerState.audio?.currentTime||0,dur:playerState.dur||0,vol:playerState.vol||1});
        setBgMiniClosed(false);
      }
      playerState.pause?.(); // ставим Player на паузу
    }
    // Если bgAudio уже играл другой проект — останавливаем его
    if(bgProj&&bgProj.id!==activeProj?.id){stopBgAudio();}
    await _loadAudioForProj(proj);
  };
  openFromUrlRef.current=handleOpen;

  // ▶ на главной: играть проект здесь же (мини-плеер), без перехода в плеер.
  const [autoPlayReq,setAutoPlayReq]=useState(0);
  const handlePlayHere=async(proj)=>{
    setMiniClosed(false);
    if(proj.id===activeProj?.id&&audioSrc){playerState.toggle?.();return;}
    if(bgProj)stopBgAudio();
    if(playerState?.playing)playerState.pause?.();
    setActiveProj(proj);
    setAutoPlayReq(Date.now());
    await _loadAudioForProj(proj);
  };

  // Аудио доиграло → следующее из очереди. В плеере он сам переключается на
  // новый выпуск (и адрес страницы тоже), на других экранах звук идёт в мини-плеере.
  queueNextRef.current=()=>{
    const next=takeNext(projects,activeProj?.id);
    if(!next)return false;
    if(screen==="player")setScreenAndHash("player",next.id,undefined,"replace");
    handlePlayHere(next);
    return true;
  };
  // Фоновое аудио доиграло: продолжаем очередь, только если человек не смотрит
  // в плеере другое аудио (не переключаем у него экран).
  bgEndedRef.current=()=>{if(screen!=="player")queueNextRef.current?.();};

  // Вспомогательная функция: загрузить и установить аудио для проекта
  const _loadAudioForProj=async(proj)=>{
    // Electron: проверяем локальный файл в Documents/AntonCards/audio/
    if(isElectron()&&proj.id){
      const localPath=await window.electronAPI.getLocalAudioPath(proj.id).catch(()=>null);
      if(localPath){setAudioSrc(`file://${localPath}`);setAudioOwnerId(proj.id);setDlStatus(prev=>({...prev,[proj.id]:"done"}));return;}
    }
    const online=await isOnline();
    const lu=await getPlayableAudioUri(proj.local_audio_filename);
    if(lu){setAudioSrc(lu);setAudioOwnerId(proj.id);}
    else{
      // Web: проверяем Cache API (офлайн-копия)
      const webCacheUrl=getWebAudioCacheUrl(proj.id);
      if(webCacheUrl){setAudioSrc(webCacheUrl);setAudioOwnerId(proj.id);}
      else if(online&&proj.audio_path){
        const publicUrl=getAudioPublicUrl(supabase,proj.audio_path);
        setAudioSrc(publicUrl);setAudioOwnerId(proj.id);
        // Копия на телефон качается следом, а не одновременно: первые секунды вся
        // скорость уходит на звук, который человек сейчас слушает.
        setTimeout(()=>ensureAudioDownloaded(proj.id,publicUrl).then(async fn=>{
          if(fn){await saveProjectLocal({...proj,local_audio_filename:fn},proj.cards||[]);setProjects(prev=>prev.map(p=>p.id===proj.id?{...p,local_audio_filename:fn}:p));setDlStatus(prev=>({...prev,[proj.id]:"done"}));}
        }),12000);
      }else{setAudioSrc(null);setAudioOwnerId(null);setScreenAndHash("upload");}
    }
  };

  // Вызывается из баннера Player — пользователь явно хочет загрузить аудио нового проекта
  const handleLoadAudio=async(proj)=>{
    if(playerState?.playing){playerState.pause?.();}
    stopBgAudio();
    await _loadAudioForProj(proj);
  };

  const handleDownload=async(proj)=>{
    if(!proj.audio_path)return;
    const publicUrl=getAudioPublicUrl(supabase,proj.audio_path);

    // ── Electron: скачать как настоящий файл в Documents/AntonCards/audio/ ──
    if(isElectron()){
      setDlStatus(prev=>({...prev,[proj.id]:0}));
      // Слушаем прогресс из главного процесса
      const onProgress=(e)=>{
        if(e.detail.projectId===proj.id)setDlStatus(prev=>({...prev,[proj.id]:e.detail.pct}));
      };
      window.addEventListener('electron_audio_progress',onProgress);
      const ext=(proj.audio_path||'').split('.').pop()||'mp3';
      const res=await window.electronAPI.downloadAudio(proj.id,publicUrl,ext).catch(()=>({ok:false}));
      window.removeEventListener('electron_audio_progress',onProgress);
      if(res?.ok){
        setDlStatus(prev=>({...prev,[proj.id]:"done"}));
        // Автоматически загружаем скачанный файл
        await _loadAudioForProj(proj);
      }else{setDlStatus(prev=>({...prev,[proj.id]:"error"}));}
      return;
    }

    // ── Проверка свободного места ──────────────────────────────────────────
    const spaceCheck=await checkStorageBeforeDownload(publicUrl);
    if(!spaceCheck.ok){
      const sizeMb=spaceCheck.fileSize?Math.round(spaceCheck.fileSize/1024/1024):null;
      const freeMb=spaceCheck.freeBytes?Math.round(spaceCheck.freeBytes/1024/1024):null;
      notify(t("Недостаточно места на диске.{0}{1} Освободите место и попробуйте снова.",[sizeMb?t(" Файл: ~{0} МБ.",[sizeMb]):"",freeMb?t(" Свободно: ~{0} МБ.",[freeMb]):""]),{tone:"error"});
      return;
    }

    if(isNative()){
      setDlStatus(prev=>({...prev,[proj.id]:0}));
      const fn=await downloadAudioWithProgress(proj.id,publicUrl,(pct)=>{
        if(pct===null)setDlStatus(prev=>({...prev,[proj.id]:"error"}));
        else setDlStatus(prev=>({...prev,[proj.id]:pct}));
      });
      if(fn){
        const updated={...proj,local_audio_filename:fn};
        await saveProjectLocal(updated,proj.cards||[]);
        setProjects(prev=>prev.map(p=>p.id===proj.id?{...p,local_audio_filename:fn}:p));
        setDlStatus(prev=>({...prev,[proj.id]:"done"}));
      }
    } else {
      if(!("caches" in window)){window.open(publicUrl,"_blank");return;}
      setDlStatus(prev=>({...prev,[proj.id]:0}));
      const ok=await downloadWebAudio(proj.id,publicUrl,(pct)=>{
        if(pct===null)setDlStatus(prev=>({...prev,[proj.id]:"error"}));
        else setDlStatus(prev=>({...prev,[proj.id]:pct}));
      });
      if(ok){setDlStatus(prev=>({...prev,[proj.id]:"done"}));}
    }
  };

  const [,confirmEl,askAsync]=useConfirm();
  const handleBulkDownload=async(fps,onProgress)=>{
    if(!session)return;
    // Only projects with audio that are not yet downloaded
    const toDownload=fps.filter(p=>p.audio_path&&dlStatus[p.id]!=="done"&&typeof dlStatus[p.id]!=="number");
    if(!toDownload.length){onProgress({done:0,total:0,failed:0,finished:true});return;}

    // Storage estimate: warn if free space < 40 MB per file + 50 MB reserve
    if(!isNative()&&"storage" in navigator){
      try{
        const est=await navigator.storage.estimate();
        const free=(est.quota||0)-(est.usage||0);
        const needed=toDownload.length*40*1024*1024+50*1024*1024;
        if(free>0&&free<needed){
          const freeMb=Math.round(free/1024/1024);
          const needMb=Math.round(needed/1024/1024);
          if(!(await askAsync({title:t("Возможно, мало места: ~{0} МБ свободно, нужно ~{1} МБ для {2} файлов. Продолжить?",[freeMb,needMb,toDownload.length]),confirmLabel:t("Продолжить"),danger:false}))){
            onProgress({done:0,total:toDownload.length,failed:0,finished:true});return;
          }
        }
      }catch{}
    }

    let done=0,failed=0;
    const total=toDownload.length;
    onProgress({done,total,failed,finished:false});

    for(const proj of toDownload){
      const publicUrl=getAudioPublicUrl(supabase,proj.audio_path);
      try{
        if(isNative()){
          setDlStatus(prev=>({...prev,[proj.id]:0}));
          const fn=await downloadAudioWithProgress(proj.id,publicUrl,(pct)=>{
            if(pct===null)setDlStatus(prev=>({...prev,[proj.id]:"error"}));
            else setDlStatus(prev=>({...prev,[proj.id]:pct}));
          });
          if(fn){
            const updated={...proj,local_audio_filename:fn};
            await saveProjectLocal(updated,proj.cards||[]);
            setProjects(prev=>prev.map(p=>p.id===proj.id?{...p,local_audio_filename:fn}:p));
            setDlStatus(prev=>({...prev,[proj.id]:"done"}));
            done++;
          }else{setDlStatus(prev=>({...prev,[proj.id]:"error"}));failed++;}
        }else{
          if(!("caches" in window)){failed++;continue;}
          setDlStatus(prev=>({...prev,[proj.id]:0}));
          const ok=await downloadWebAudio(proj.id,publicUrl,(pct)=>{
            if(pct===null)setDlStatus(prev=>({...prev,[proj.id]:"error"}));
            else setDlStatus(prev=>({...prev,[proj.id]:pct}));
          });
          if(ok){setDlStatus(prev=>({...prev,[proj.id]:"done"}));done++;}
          else{setDlStatus(prev=>({...prev,[proj.id]:"error"}));failed++;}
        }
      }catch(e){
        console.error("[BulkDownload]",proj.id,e);
        setDlStatus(prev=>({...prev,[proj.id]:"error"}));
        failed++;
      }
      onProgress({done,total,failed,finished:false});
    }
    onProgress({done,total,failed,finished:true});
  };

  const handleDeleteOffline=async(proj)=>{
    if(isNative()){
      await deleteNativeAudio(proj.local_audio_filename);
      const updated={...proj,local_audio_filename:null};
      await saveProjectLocal(updated,proj.cards||[]);
      setProjects(prev=>prev.map(p=>p.id===proj.id?{...p,local_audio_filename:null}:p));
      // Если плеер играл этот файл — переключить на streaming
      if(activeProj?.id===proj.id&&proj.audio_path){
        const online=await isOnline();
        if(online){setAudioSrc(getAudioPublicUrl(supabase,proj.audio_path));}else{setAudioSrc(null);}
      }
    } else {
      await deleteWebAudio(proj.id);
      if(activeProj?.id===proj.id&&proj.audio_path){
        const online=await isOnline();
        if(online){setAudioSrc(getAudioPublicUrl(supabase,proj.audio_path));}else{setAudioSrc(null);}
      }
    }
    setDlStatus(prev=>({...prev,[proj.id]:"idle"}));
  };

  // Правки в плеере: сначала на устройство, на сервер — через очередь отправки
  // и только то, что изменилось (раньше каждое изменение слало все карточки проекта
  // и терялось без интернета или при ошибке запроса).
  const handleSave=useCallback(async(cards,name,meta={})=>{
    if(!activeProj)return;
    const projId=activeProj.id;
    // Плеер сохраняет карточки другого проекта (успел смениться) — ничего не трогаем.
    if(meta.projectId&&meta.projectId!==projId){reportError("save-mismatch",new Error(`player ${meta.projectId} vs active ${projId}`));return;}
    const newName=name||activeProj.name;
    await saveProjectLocal({...activeProj,name:newName},cards);
    if(newName!==activeProj.name)enqueue("project.update","project:"+projId,{id:projId,fields:{name:newName,updated_at:new Date().toISOString()}});
    const prevById=new Map((activeProj.cards||[]).map(c=>[c.id,c]));
    for(const c of cards){
      const o=prevById.get(c.id);
      if(o&&o.time_sec===c.time_sec&&o.word===c.word&&(o.translation||"")===(c.translation||"")&&o.color===c.color)continue;
      enqueue("cards.upsert","card:"+c.id,{id:c.id,project_id:projId,time_sec:c.time_sec,word:c.word,translation:c.translation||"",color:c.color});
    }
    // Удаления — только те, что плеер явно сделал. Больше 5 разом — лишь по команде
    // «Удалить выбранные» (с подтверждением); иначе это сбой, а не желание человека.
    const removed=(meta.removedIds||[]).filter(id=>prevById.has(id));
    if(removed.length>5&&!meta.bulk){
      reportError("save-mass-delete-blocked",new Error(`${removed.length} of ${prevById.size} cards in ${projId}`));
    }else{
      for(const id of removed)enqueue("cards.delete","card:"+id,{id});
    }
    flushSoon();
    setActiveProj(p=>({...p,cards,name:newName}));
    setProjects(prev=>prev.map(p=>p.id===projId?{...p,cards,cardCount:cards.length,name:newName}:p));
  },[activeProj]);

  // Push all timecoded cards to overlay when project changes
  useEffect(()=>{
    if(!isElectron()||!activeProj?.cards)return;
    const sorted=[...activeProj.cards].sort((a,b)=>(a.time_sec||0)-(b.time_sec||0));
    window.electronAPI.sendAllCards(sorted);
    // Оверлей, открытый позже, попросит карточки сам.
    window.__acOverlayResend=()=>window.electronAPI.sendAllCards(sorted);
  },[activeProj?.id,activeProj?.cards?.length]);// eslint-disable-line react-hooks/exhaustive-deps

  const [playerState,setPlayerState]=useState({playing:false,cur:0,dur:0});
  const audioPlayingRef=useRef(false);
  useEffect(()=>{audioPlayingRef.current=playerState.playing;},[playerState.playing]);
  const [miniClosed,setMiniClosed]=useState(false);
  // Reset when new project opened OR when returning from player screen
  useEffect(()=>{setMiniClosed(false);},[activeProj?.id]);
  useEffect(()=>{if(screen==="player")setMiniClosed(false);},[screen]);

  // Нотификация тапнута → переходим на экран плеера (если аудио загружено).
  // MainActivity.onNewIntent диспатчит это событие когда приложение в фоне.
  useEffect(()=>{
    const handler=()=>{
      if(activeProj&&playerState.dur>0&&screen!=="player"){
        setScreenAndHash("player",activeProj.id);
      }
    };
    document.addEventListener("notification-tap",handler);
    return()=>document.removeEventListener("notification-tap",handler);
  },[activeProj,playerState.dur,screen,setScreenAndHash]);

  // Оценки в повторении и правки карточек из квиза: в очередь отправки уходят
  // только изменённые карточки (раньше — весь проект на каждую оценку).
  const handleSaveProject=useCallback(async(projId,cards)=>{
    const proj=projects.find(p=>p.id===projId);if(!proj)return;
    const updated={...proj,cards,cardCount:cards.length};
    setProjects(prev=>prev.map(p=>p.id===projId?updated:p));
    await saveProjectLocal(updated,cards);
    const prevById=new Map((proj.cards||[]).map(c=>[c.id,c]));
    for(const c of cards){
      const o=prevById.get(c.id);
      if(o&&CARD_FIELDS.every(f=>(o[f]??null)===(c[f]??null)))continue;
      enqueue("cards.upsert","card:"+c.id,{...srsRow(projId,c),time_sec:c.time_sec,word:c.word,translation:c.translation||"",color:c.color});
    }
    flushSoon();
  },[projects]);

  // Изменение полей проекта: на устройстве сразу, на сервер — через очередь отправки.
  const updateProjectFields=useCallback((id,fields)=>{
    const proj=projects.find(p=>p.id===id);
    setProjects(prev=>prev.map(p=>p.id===id?{...p,...fields}:p));
    if(proj)saveProjectLocal({...proj,...fields},proj.cards||[]).catch(()=>{});
    enqueue("project.update","project:"+id,{id,fields:{...fields,updated_at:new Date().toISOString()}});
    flushSoon();
  },[projects]);

  const handleRename=useCallback((id,name)=>updateProjectFields(id,{name}),[updateProjectFields]);

  // ── Удаление с «Отменить» (аудио, тексты, папки) — app/useUndoDeletes ──────
  // Должно идти ДО handleDelete/handleDeleteFolder/handleDeleteTextProject: они
  // берут enqueueDelete в зависимости useCallback (иначе — ошибка TDZ).
  const deletes=useUndoDeletes();
  const enqueueDelete=deletes.enqueue;

  const handleDelete=useCallback((id)=>{
    const proj=projects.find(p=>p.id===id);
    if(!proj)return;
    // Optimistic UI: убираем карточку из списка. Сервер/локальная БД пока не
    // трогаем — реальный delete только после 6-сек окна undo. Если юзер жмёт
    // Undo, просто возвращаем проект в state — данные на сервере и в локальной
    // БД всё это время никуда не девались.
    setProjects(prev=>prev.filter(p=>p.id!==id));
    enqueueDelete({
      type:'audio', id,
      label:t("Аудио «{0}» удалён",[proj.name||t("Без названия")]),
      restore:()=>setProjects(prev=>[proj,...prev]),
      commit:async()=>{
        try{
          // Без интернета удаление тоже дойдёт до сервера (раньше проект возвращался при следующей синхронизации).
          enqueue("project.delete","project:"+id,{id,audio_path:proj.audio_path||null});
          await deleteProjectLocal(id);
          flushSoon(300);
        }catch(e){console.warn('[delete audio]:',e);}
      },
    });
  },[projects,enqueueDelete]);

  const handleMoveToFolder=useCallback((id,folder)=>updateProjectFields(id,{folder:folder||null}),[updateProjectFields]);

  // Назначение языка проекту. Local-first: React-state + localStorage синхронно/быстро,
  // Supabase fire-and-forget в фоне — поэтому массовое присвоение в визарде не тормозит.
  const handleAssignLang=useCallback((projId,lang,isText)=>{
    const L=(lang||'').toLowerCase();
    const uid=session?.user?.id;
    if(isText){
      let upd=null;
      setTextProjects(prev=>{
        const i=prev.findIndex(p=>p.id===projId);
        if(i<0)return prev;
        upd={...prev[i],src_lang:L};
        const next=[...prev];next[i]=upd;return next;
      });
      // Локально + Supabase — оба в фон, не блокируют визард.
      if(upd&&uid){upsertTextProject(uid,upd).catch(e=>console.warn('[lang] text upsert:',e?.message));}
    }else{
      let upd=null;
      setProjects(prev=>{
        const i=prev.findIndex(p=>p.id===projId);
        if(i<0)return prev;
        upd={...prev[i],src_lang:L,updated_at:new Date().toISOString()};
        const next=[...prev];next[i]=upd;return next;
      });
      if(upd){
        // Локалка — асинх но быстрая; не ждём (на web — мгновенно, на native — десятки мс).
        saveProjectLocal(upd,upd.cards||[]).catch(e=>console.warn('[lang] saveLocal:',e?.message));
        // Сервер — через очередь отправки, не блокирует визард.
        enqueue("project.update","project:"+projId,{id:projId,fields:{src_lang:L,updated_at:upd.updated_at}});
        flushSoon();
      }
    }
  },[session]);

  const handleCreateFolder=useCallback((name)=>{
    if(!session)return;saveLocalFolder(session.user.id,name);setExtraFolders(prev=>prev.includes(name)?prev:[...prev,name]);
  },[session]);

  const handleDeleteFolder=useCallback((name)=>{
    if(!session)return;
    const uid=session.user.id;
    // Запоминаем проекты которые лежали в этой папке — на undo вернём им folder.
    // Раньше delFolderConfirm коммитил move-out сразу через onMoveToFolder,
    // и undo восстанавливал только саму папку — проекты оставались в корне.
    const affected=projects.filter(p=>p.folder===name).map(p=>p.id);
    const projectsAtDelete=projects;
    // Optimistic UI: убираем папку и сбрасываем folder у её проектов.
    setExtraFolders(prev=>prev.filter(f=>f!==name));
    setProjects(prev=>prev.map(p=>p.folder===name?{...p,folder:null}:p));
    enqueueDelete({
      type:'folder', id:name,
      label:t("Папка «{0}» удалена",[name]),
      restore:()=>{
        setExtraFolders(prev=>prev.includes(name)?prev:[...prev,name]);
        setProjects(prev=>prev.map(p=>affected.includes(p.id)?{...p,folder:name}:p));
      },
      commit:async()=>{
        deleteLocalFolder(uid,name);
        // Серверная синхронизация move-out (folder=null) — только при коммите, через очередь отправки.
        const now=new Date().toISOString();
        for(const id of affected){
          enqueue("project.update","project:"+id,{id,fields:{folder:null,updated_at:now}});
          const pr=projectsAtDelete.find(p=>p.id===id);
          if(pr)saveProjectLocal({...pr,folder:null},pr.cards||[]).catch(()=>{});
        }
        flushSoon(300);
      },
    });
  },[session,enqueueDelete,projects]);

  const handleLogout=()=>{try{localStorage.removeItem("ac_pro");}catch{}setIsPro(null);signOutNow(session?.access_token);_memProjCache={userId:null,projects:null};setProjects([]);setExtraFolders([]);setActiveProj(null);setAudioSrc(null);setAudioOwnerId(null);setTextProjects([]);setActiveTextProj(null);};

  const [showAccount,setShowAccount]=useState(false);
  const [recovery,setRecovery]=useState(false); // перешли по ссылке «сбросить пароль» из письма
  useEffect(()=>{
    let tok=null;try{tok=JSON.parse(sessionStorage.getItem("ac_recovery")||"null");sessionStorage.removeItem("ac_recovery");}catch{}
    if(!tok)return;
    supabase.auth.setSession(tok).then(({error})=>{
      if(error){try{sessionStorage.setItem("ac_auth_link_error","expired");}catch{}return;}
      setRecovery(true);
    });
  },[]);
  const [importBookOpen,setImportBookOpen]=useState(false);
  // Админ-панель: кнопка 🛡 видна только тем, кто есть в таблице admins.
  const [admin,setAdmin]=useState(false);
  const [adminOpen,setAdminOpen]=useState(false);
  useEffect(()=>{
    const uid=session?.user?.id;
    if(!uid){setAdmin(false);return;}
    touchProfile(uid);
    let stop=false;
    // Pro = тариф Pro (не истёк) или админ. Не удалось узнать (нет сети) — остаётся прежнее.
    Promise.all([checkIsAdmin().then(v=>({ok:true,v:!!v}),()=>({ok:false})),getProfile(uid).catch(()=>null)]).then(([adm,prof])=>{
      if(stop)return;
      setAdmin(adm.ok?adm.v:false);
      if(prof?.name)setUserName(prof.name);
      if(!adm.ok)return;
      const planOk=prof?.plan==="pro"&&(!prof.plan_until||new Date(prof.plan_until)>new Date());
      const pro=adm.v||planOk;
      setIsPro(pro);
      try{localStorage.setItem("ac_pro",pro?"1":"0");}catch{}
    });
    return()=>{stop=true;};
  },[session?.user?.id]);

  // Телефон: браузер вернул в приложение после входа через Google — завершаем вход.
  useEffect(()=>onDeepLink(async(url)=>{
    if(!url.startsWith("com.audiocards.app://auth-callback"))return;
    try{
      const u=new URL(url.replace("com.audiocards.app://","https://x/"));
      const code=u.searchParams.get("code");
      const errText=u.searchParams.get("error_description")||u.searchParams.get("error");
      if(code){const{error}=await supabase.auth.exchangeCodeForSession(code);if(error)throw error;}
      else if(errText)throw new Error(errText);
    }catch(e){notify(t("Не удалось завершить вход: {0}",[e?.message||String(e)]),{tone:"error"});}
  }),[]);

  // Блокировка и «выкинуть» из админ-панели, объявление для всех: проверяем при входе,
  // при возврате в приложение и раз в 5 минут.
  const [announcement,setAnnouncement]=useState(null);
  const dismissAnnouncement=useCallback(()=>{
    if(announcement){try{localStorage.setItem("ac_ann_dismissed",String(announcement.id));}catch{}}
    setAnnouncement(null);
  },[announcement]);
  useEffect(()=>{
    const uid=session?.user?.id;if(!uid)return;
    try{if(!localStorage.getItem("ac_login_at"))localStorage.setItem("ac_login_at",new Date().toISOString());}catch{}
    let stop=false;
    const check=async()=>{
      const st=await myStatus();
      if(stop)return;
      if(st){
        const banned=st.banned_until&&new Date(st.banned_until)>new Date();
        let loginAt="";try{loginAt=localStorage.getItem("ac_login_at")||"";}catch{}
        const kicked=st.kicked_at&&loginAt&&new Date(st.kicked_at)>new Date(loginAt);
        if(banned||kicked){
          try{
            localStorage.setItem("ac_auth_notice",JSON.stringify(banned?{kind:"ban",until:st.banned_until,reason:st.ban_reason||""}:{kind:"kick"}));
            localStorage.removeItem("ac_login_at");
          }catch{}
          handleLogout();
          return;
        }
      }
      const a=await getAnnouncement();
      if(stop)return;
      let dismissed="";try{dismissed=localStorage.getItem("ac_ann_dismissed")||"";}catch{}
      setAnnouncement(a&&String(a.id)!==dismissed?a:null);
    };
    check();
    const id=setInterval(()=>{if(document.visibilityState==="visible")check();},5*60*1000);
    const onVis=()=>{if(document.visibilityState==="visible")check();};
    document.addEventListener("visibilitychange",onVis);
    return()=>{stop=true;clearInterval(id);document.removeEventListener("visibilitychange",onVis);};
  },[session?.user?.id]);// eslint-disable-line react-hooks/exhaustive-deps
  // Книга из файла: каждая глава — текст в папке книги. Глава 1 получает самую позднюю
  // дату создания, чтобы в сортировке «Новые» главы шли по порядку.
  const handleImportBook=useCallback(async({book,lang,chapters},onProgress)=>{
    const uid=session?.user?.id;if(!uid)return;
    const base=Date.now();const made=[];
    for(let i=0;i<chapters.length;i++){
      onProgress?.(i+1,chapters.length);
      const at=new Date(base-i*1000).toISOString();
      const proj=createTextProject({name:chapters[i].title,content:chapters[i].content,folder:book,src_lang:lang,created_at:at,updated_at:at});
      made.push(await upsertTextProject(uid,proj));
    }
    setTextProjects(prev=>[...made,...prev]);
  },[session]);
  const [showFsrsSettings,setShowFsrsSettings]=useState(false);
  // Tracks whether any "owning" bottom-sheet is currently open (e.g. SrsPanel
  // on mobile). When true, MiniPlayer hides itself — Spotify-style. The sheet
  // dispatches `ac:bottom-sheet` with {open: true/false} when it mounts/closes.
  // Карточки изучаемого языка — сводка дня в боковом меню «Колоды».
  const langCards=useMemo(()=>projects.filter(p=>!activeLang||(p.src_lang||'').toLowerCase()===activeLang).flatMap(p=>p.cards||[]),[projects,activeLang]);
  // Слова для автомата в боковом меню («Аркада»): карточки изучаемого языка со словом и переводом.
  const slotWords=useMemo(()=>{
    const list=[];
    for(const p of projects){
      if(activeLang&&(p.src_lang||'').toLowerCase()!==activeLang)continue;
      for(const c of p.cards||[])if(c.word&&c.translation&&String(c.word).length<=24)list.push({word:String(c.word).trim(),translation:String(c.translation).trim()});
      if(list.length>=400)break;
    }
    return list;
  },[projects,activeLang]);
  const [bottomSheetOpen,setBottomSheetOpen]=useState(false);
  useEffect(()=>{
    const onSheet=(e)=>setBottomSheetOpen(!!e?.detail?.open);
    window.addEventListener('ac:bottom-sheet',onSheet);
    return()=>window.removeEventListener('ac:bottom-sheet',onSheet);
  },[]);

  // Полная синхронизация: кнопка, «потянуть вниз», сама при возврате и раз в 5 минут.
  const handleManualSync=useManualSync(session,syncStatus,{
    onProjects:(list,uid)=>{_memProjCache={userId:uid,projects:list};setProjects(list);},
    onTexts:setTextProjects,
  });
  const manualSyncRef=useRef(handleManualSync);
  manualSyncRef.current=handleManualSync;

  // ── Text project handlers ────────────────────────────────────────────────────
  // Новые тексты этого сеанса: если из такого ушли пустым, он удаляется (см. ReaderScreen).
  const freshTextIdsRef=useRef(new Set());
  const handleLeaveEmptyText=useCallback((projId)=>{
    if(!freshTextIdsRef.current.has(projId))return;
    // Проверяем после отрисовки: в режиме разработки React «размонтирует» экран
    // понарошку, а мы всё ещё в этом тексте — тогда ничего не удаляем.
    setTimeout(()=>{
      if(screenRef.current==="reader"&&window.location.hash.includes(projId))return;
      freshTextIdsRef.current.delete(projId);
      setTextProjects(prev=>prev.filter(p=>p.id!==projId));
      setActiveTextProj(a=>a?.id===projId?null:a);
      try{if(localStorage.getItem("ac_last_text")===projId)localStorage.removeItem("ac_last_text");}catch{}
      deleteTextProject(session?.user?.id,projId).catch(()=>{});
    },0);
  },[session]);
  const handleNewTextProject=useCallback(async(folder='')=>{
    const al=getActiveLang();
    const proj=createTextProject({folder:folder||'',...(al?{src_lang:al}:{})});
    const saved=await upsertTextProject(session?.user?.id,proj);
    freshTextIdsRef.current.add(saved.id);
    setTextProjects(prev=>[saved,...prev]);
    setActiveTextProj(saved);
    try{localStorage.setItem("ac_reader_proj",saved.id);localStorage.setItem("ac_last_text",saved.id);}catch{}
    setScreenAndHash("reader",saved.id);
  },[session,setScreenAndHash]);

  const handleOpenTextProject=useCallback((proj)=>{
    setActiveTextProj(proj);
    try{localStorage.setItem("ac_reader_proj",proj.id);localStorage.setItem("ac_last_text",proj.id);}catch{}
    setScreenAndHash("reader",proj.id);
  },[setScreenAndHash]);

  // ── Нижняя панель (телефон) ────────────────────────────────────────────────
  const [navMode,setNavMode]=useState(()=>window.innerWidth<768);
  useEffect(()=>{const h=()=>setNavMode(window.innerWidth<768);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  // ── Боковая панель (компьютер, планшет): от 1200 px — с подписями, уже — узкая полоса ──
  const [sideCompact,setSideCompact]=useState(()=>window.innerWidth<1200);
  useEffect(()=>{const h=()=>setSideCompact(window.innerWidth<1200);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  const [newFolderReq,setNewFolderReq]=useState(0);
  // «Новый»: меню рядом с кнопкой (компьютер) или лист снизу (телефон).
  const [newMenu,setNewMenu]=useState(null); // {at|sheet, folder}
  const openNewMenu=useCallback((e,folder=null)=>{
    const el=e?.currentTarget;
    if(navMode){setNewMenu({sheet:true,folder});return;}
    const r=el?.getBoundingClientRect?.();
    // Кнопка в боковой панели — меню справа от неё и вверх; в странице — под кнопкой.
    const inSide=!!el?.closest?.("nav");
    const at=inSide&&r?{bottom:Math.max(8,window.innerHeight-r.bottom),left:r.right+8}:anchorOf(el,250);
    setNewMenu({at,folder});
  },[navMode]);
  // На значке «Повторение» — сколько реально на сегодня (с дневными лимитами), а не
  // все новые карточки подряд: 529 «долгов» отбивали желание начинать.
  const due=useMemo(()=>{const p=todayPlan(projects.filter(p=>!activeLang||(p.src_lang||'').toLowerCase()===activeLang).flatMap(p=>p.cards||[]));return{repeat:p.learn+p.review,fresh:p.fresh};},[projects,activeLang]);
  // «Главная»: из другого раздела — на главную; на главной — закрыть папку и поиск, затем наверх.
  const goHome=useCallback(()=>{
    if(screen!=="projects"){setScreenAndHash("projects");return;}
    if(activeFolder||searchQuery){navProjects({folder:null,search:null});return;}
    window.scrollTo({top:0,behavior:"smooth"});
  },[screen,activeFolder,searchQuery,navProjects,setScreenAndHash]);
  const startNewAudio=useCallback((folder)=>{
    setActiveProj(null);setAudioSrc(null);setScreenAndHash("upload");setPendingFolder(folder||null);
  },[setScreenAndHash]);
  // Сколько проектов на каждом языке — выбор языка показывает языки с контентом первыми.
  const langCounts=useMemo(()=>{
    const c={};
    [...projects,...textProjects].forEach(p=>{const l=(p.src_lang||'').toLowerCase();if(l)c[l]=(c[l]||0)+1;});
    return c;
  },[projects,textProjects]);
  // Повторение одно: и с главной, и из раздела, и из проекта — та же очередь FSRS.
  // Игры для запоминания; leech — сразу с пиявками (часто забываемыми карточками).
  const openGames=useCallback((leech)=>{
    setGamesLeech(!!leech);setScreenAndHash("games",leech?"leeches":null);
  },[setScreenAndHash]);
  const openQuiz=useCallback((id)=>{
    setQuizProjId(id);setQuizAutoSrs(true);setScreenAndHash("quiz");
    try{sessionStorage.setItem("ac_quiz_nav",JSON.stringify({projId:id,autoSrs:true}));}catch{}
  },[setScreenAndHash]);
  // «Повторение»: список колод с карточками на сегодня (как главный экран Anki).
  // Если повторять нечего — обычный квиз по всем карточкам.
  const handleSaveTextProject=useCallback(async(proj)=>{
    if(!session?.user?.id)return;
    const saved=await upsertTextProject(session.user.id,proj);
    setTextProjects(prev=>{
      const i=prev.findIndex(p=>p.id===proj.id);
      if(i>=0){const n=[...prev];n[i]=saved;return n;}
      return[saved,...prev];
    });
    if(activeTextProj?.id===proj.id)setActiveTextProj(saved);
  },[session,activeTextProj?.id]);

  const handleDeleteTextProject=useCallback((projId)=>{
    const proj=textProjects.find(p=>p.id===projId);
    if(!proj)return;
    // Optimistic remove from UI
    setTextProjects(prev=>prev.filter(p=>p.id!==projId));
    if(activeTextProj?.id===projId){setActiveTextProj(null);setScreenAndHash("projects");}
    enqueueDelete({
      type:'text', id:projId,
      label:t("Текст «{0}» удалён",[proj.name||t("Без названия")]),
      restore:()=>setTextProjects(prev=>[proj,...prev]),
      commit:()=>{deleteTextProject(session?.user?.id,projId).catch(()=>{});},
    });
  },[textProjects,activeTextProj?.id,session,setScreenAndHash,enqueueDelete]);

  // ── Очистить статистику (язык, «без языка», всю) с «Отменить» ─────────────
  const clearStats=useClearStats(session);

  const handleRenameTextProject=useCallback(async(projId,newName)=>{
    const proj=textProjects.find(p=>p.id===projId);
    if(!proj||!newName.trim())return;
    await handleSaveTextProject({...proj,name:newName.trim()});
  },[textProjects,handleSaveTextProject]);

  const handleMoveTextToFolder=useCallback(async(projId,folder)=>{
    const proj=textProjects.find(p=>p.id===projId);
    if(!proj)return;
    await handleSaveTextProject({...proj,folder:folder||''});
  },[textProjects,handleSaveTextProject]);

  // Текст прямо в аудиопроекте (#188): новый текстовый проект, сразу привязанный к аудио.
  const handleCreateLinkedText=useCallback(async(audioProj,content)=>{
    const text=String(content||"").trim();
    if(!audioProj||!text)return;
    await handleSaveTextProject(createTextProject({
      name:audioProj.name||t("Текст"),content:text,folder:audioProj.folder||"",linked_audio_id:audioProj.id,
      ...(audioProj.src_lang?{src_lang:audioProj.src_lang}:{}),
    }));
  },[handleSaveTextProject]);

  const handleLinkTextToAudio=useCallback(async(textProjId,audioProjId)=>{
    const tp=textProjects.find(p=>p.id===textProjId);
    if(!tp)return;
    await handleSaveTextProject({...tp,linked_audio_id:audioProjId||null});
  },[textProjects,handleSaveTextProject]);

  // «←» на экранах с фокусом: туда, откуда пришли, а если экран открыт по ссылке —
  // в его раздел (без лишней записи в истории).
  const goBack=useCallback((parent)=>{
    if(histIdx()>0){try{window.history.back();return;}catch{}}
    setScreenAndHash(parent,null,undefined,"replace");
  },[setScreenAndHash]);
  const goBackRef=useRef(goBack);
  useEffect(()=>{goBackRef.current=goBack;},[goBack]);

  const handlePlayerBack=useCallback(()=>{
    // Сбрасываем pending — иначе _hasPendingPlayer заморозит экран проектов
    try{localStorage.removeItem("ac_pending_player");}catch{}
    goBack("projects");
    loadProjects(false);
  },[loadProjects,goBack]);

  // ── Кнопка «Назад» (Android) и стрелки браузера ─────────────────────────────
  // Браузер: адрес — источник правды. На popstate (стрелки браузера, «←» в
  // приложении, history.back с телефона) приводим экран к адресу; сами ничего в
  // историю не пишем, иначе ломается «вперёд».
  // Android «Назад» — как у всех приложений: закрыть всплывающее окно → выйти из
  // экрана с фокусом → из раздела в Библиотеку → закрыть поиск/папку → свернуть
  // приложение (без вопроса «Выйти?»; звук продолжает играть).
  // Всплывающие окна встают в очередь «Назад» через ui/useBackClose;
  // в историю они больше не пишут (раньше каждое касание слова в читалке
  // добавляло запись, и «Назад» в браузере «ничего не делал»).
  const screenRef=useRef(screen);
  useEffect(()=>{screenRef.current=screen;},[screen]);
  const projectsRef=useRef(projects);
  useEffect(()=>{projectsRef.current=projects;},[projects]);
  const textProjectsRef=useRef(textProjects);
  useEffect(()=>{textProjectsRef.current=textProjects;},[textProjects]);

  useEffect(()=>{
    const isNativeApp=()=>typeof window!=="undefined"&&window.Capacitor?.isNativePlatform?.();

    const syncFromUrl=()=>{
      const {scr,id,lang,folder,search}=parseRoute(window.location.hash);
      if(scr==="projects"){
        const normLang=(lang||"").toLowerCase();
        if(normLang !== activeLangRef.current){ saveActiveLang(normLang); setActiveLang(normLang); }
        if((folder||null) !== (activeFolderRef.current||null)) setActiveFolder(folder||null);
        if((search||"")   !== (searchQueryRef.current||""))    setSearchQuery(search||"");
      }
      if(scr==="player"){
        const p=id&&(projectsRef.current||[]).find(pp=>pp.id===id);
        // «Назад/Вперёд» к другому аудио — открываем как обычно (со звуком), а не
        // только меняем экран: иначе плеер показывал «0:00 / 0:00» без звука.
        if(p){if(openFromUrlRef.current)openFromUrlRef.current(p);else{setActiveProj(p);setScreen("player");}}
        else if(id){try{localStorage.setItem("ac_pending_player",id);}catch{}setScreen("projects");}
        else setScreen("projects");
        return;
      }
      if(scr==="reader"){
        const p=id&&(textProjectsRef.current||[]).find(pp=>pp.id===id);
        if(p){setActiveTextProj(p);setScreen("reader");}else setScreen("reading");
        return;
      }
      // upload / vocab_quiz can't be hydrated from URL alone — coerce to a section.
      if(scr==="upload"){setScreen("projects");return;}
      if(scr==="vocab_quiz"){setScreen("review");return;}
      if(scr==="games")setGamesLeech(id==="leeches");
      setScreen(scr);
    };

    const minimizeApp=()=>{
      if(nativeMinimize())return;
      if(typeof window.AndroidApp?.minimizeApp==="function"){window.AndroidApp.minimizeApp();return;}
      if(window.Capacitor?.Plugins?.App?.minimizeApp){window.Capacitor.Plugins.App.minimizeApp();return;}
      if(typeof window.AndroidApp?.exitApp==="function")window.AndroidApp.exitApp();
    };

    const onHardwareBack=()=>{
      // 1. Открытое всплывающее окно (последнее открытое — первым).
      if(closeTopOnBack())return;
      const cur=screenRef.current;
      // 2. Экран с фокусом — туда, откуда пришли.
      if(cur==="player"){handlePlayerBack();return;}
      const PARENT={reader:"reading",quiz:"review",games:"review",vocab_quiz:"review",upload:"projects"};
      if(PARENT[cur]){goBackRef.current(PARENT[cur]);return;}
      // 3. Другой раздел — в Библиотеку.
      if(cur!=="projects"){setScreenAndHash("projects",null,undefined,"replace");return;}
      // 4. Библиотека: сначала поиск и папка, потом — свернуть.
      if(searchQueryRef.current){setSearchQuery("");histReplace(buildHash("projects",null,{lang:activeLangRef.current||null,folder:activeFolderRef.current||null}));return;}
      if(activeFolderRef.current){setActiveFolder(null);histReplace(buildHash("projects",null,{lang:activeLangRef.current||null}));return;}
      minimizeApp();
    };

    window.addEventListener("popstate",syncFromUrl);
    if(isNativeApp())document.addEventListener("backbutton",onHardwareBack,false);
    return()=>{
      window.removeEventListener("popstate",syncFromUrl);
      document.removeEventListener("backbutton",onHardwareBack,false);
    };
  },[handlePlayerBack,setScreenAndHash]);

  // ── Потянуть вниз — синхронизация (на разделах) ───────────────────────────
  const ptr=usePullToRefresh(NAV_SCREENS.has(screen),()=>manualSyncRef.current?.());


  // Text project linked to the active audio project
  const linkedTextProject=useMemo(()=>textProjects.find(p=>p.linked_audio_id===activeProj?.id)||null,[textProjects,activeProj?.id]);

  // Suppress projects flash when navigating to player on refresh
  const _hasPendingPlayer=(()=>{try{return screen==="projects"&&!!localStorage.getItem("ac_pending_player");}catch{return false;}})();

  // ── MiniPlayer presence → CSS var on :root ───────────────────────────────
  // Mirrors exactly the render conditions for the two MiniPlayer instances.
  // Any screen needing bottom breathing room reads `--ac-mini-h` and adds it
  // to its own padding-bottom. Avoids prop-drilling a flag into every screen.
  const _miniVisible = !bottomSheetOpen && (
    (bgProj && bgState.dur>0 && !bgMiniClosed) ||
    (screen!=="player" && activeProj && audioSrc && playerState.dur>0 && !miniClosed && !bgProj)
  );
  useEffect(()=>{
    // Empirically: 28px seek + ~50px controls + 8px pad ≈ 90px. Round to 96.
    // Высоту ставит сам мини-плеер (на телефоне он в две строки); здесь — только сброс.
    if(!_miniVisible)document.documentElement.style.setProperty('--ac-mini-h','0px');
  },[_miniVisible]);

  const pal=THEMES[theme]||THEMES.light;
  if(screen==="loading"||_hasPendingPlayer)return(<div style={{minHeight:"100vh",background:THEMES[theme].pageBg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:FONT,fontSize:20,color:THEMES[theme].blue}}><Icon n="cloud" style={{marginRight:6}}/>{t("Загрузка…")}</div>);
  if(screen==="auth")return<ThemeContext.Provider value={pal}><AuthScreen theme={theme} onTheme={applyTheme}/><Toaster/></ThemeContext.Provider>;

  return(
    <ThemeContext.Provider value={pal}>
    <WordLearningProvider userId={session?.user?.id}>
    <OverlayBridge/>
    <>
      {showLangIntro&&<LangIntroModal theme={theme} onPick={closeLangIntro} onSkip={()=>closeLangIntro(null)}/>}
      {adminOpen&&<Suspense fallback={null}><AdminPanel pal={THEMES[theme]||THEMES.light} onClose={()=>setAdminOpen(false)}/></Suspense>}
      {queueOpen&&<QueueSheet C={THEMES[theme]||THEMES.light} projects={projects}
        pickable={projects.filter(p=>(p.audio_path||p.local_audio_filename)&&(!activeLang||(p.src_lang||"").toLowerCase()===activeLang))}
        current={bgProj||(audioSrc?activeProj:null)} playing={bgProj?bgState.playing:!!playerState.playing}
        onPlay={(p)=>{setQueueOpen(false);if(screen==="player")setScreenAndHash("player",p.id,undefined,"replace");handlePlayHere(p);}}
        onClose={()=>setQueueOpen(false)}/>}
      {importBookOpen&&<Suspense fallback={null}><ImportBook pal={THEMES[theme]||THEMES.light} defaultLang={activeLang||"es"} onImport={handleImportBook} onClose={()=>setImportBookOpen(false)}/></Suspense>}
      {recovery&&session&&<NewPasswordModal theme={theme} email={session?.user?.email||""} onClose={()=>setRecovery(false)}/>}
      {showAccount&&<AccountModal email={session?.user?.email||""} theme={theme} onTheme={applyTheme} isPro={isPro===true} onClose={()=>setShowAccount(false)}
        onForceLogoutAll={()=>{setShowAccount(false);handleLogout();}} onLogout={()=>{setShowAccount(false);handleLogout();}}
        onOpenFsrsSettings={()=>{setShowAccount(false);setShowFsrsSettings(true);}}
        activeLang={activeLang} onActiveLang={applyActiveLang} langCounts={langCounts}/>}
      {showFsrsSettings&&<FsrsSettings onClose={()=>setShowFsrsSettings(false)}/>}
      {confirmEl}
      <Toaster/>
      {/* Единый стэк undo-тостов: тексты, аудио, папки. Index 0 = нижний.
          Каждый Undo вызывает restore этого entry; commit идёт по своему
          таймеру (или принудительно на beforeunload). */}
      {deletes.items.map((entry,i)=>(
        <UndoToast key={entry.key}
          label={entry.label}
          onUndo={()=>deletes.undo(entry.key)}
          stackIndex={deletes.items.length-1-i}/>
      ))}
      {clearStats.pending&&<UndoToast label={clearStats.pending.label} onUndo={clearStats.undo} stackIndex={deletes.items.length}/>}
      {screen==="reader"&&(
        <Suspense fallback={
          <div style={{position:"fixed",inset:0,display:"flex",alignItems:"center",justifyContent:"center",
            background:THEMES[theme]?.pageBg||"#fbf8f2",zIndex:50}}>
            <div style={{width:36,height:36,border:`3px solid ${THEMES[theme]?.border||"#ddd"}`,
              borderTopColor:THEMES[theme]?.blue||"#2344a0",borderRadius:"50%",
              animation:"spin 0.8s linear infinite"}}/>
          </div>
        }>
          <ReaderScreen theme={theme} onTheme={applyTheme} project={activeTextProj} onSave={handleSaveTextProject} onLeaveEmpty={handleLeaveEmptyText}
            onBack={()=>{try{localStorage.removeItem("ac_reader_proj");}catch{}goBack("reading");}}
/>
        </Suspense>
      )}
      <Player project={activeProj} audioSrc={audioSrc} audioOwnerId={audioOwnerId} onSave={handleSave} onBack={handlePlayerBack}
        linkedTextProject={linkedTextProject}
        onOpenTextProject={handleOpenTextProject}
        onLinkTextToAudio={handleLinkTextToAudio}
        onCreateLinkedText={handleCreateLinkedText}
        textProjects={textProjects}
        onQuiz={openQuiz}
        theme={theme} onTheme={applyTheme} visible={screen==="player"}
        onStateChange={setPlayerState}
        onLoadAudio={handleLoadAudio}
        onBeforePlay={stopBgAudio}
        downloadStatus={dlStatus}
        onDownload={handleDownload}
        onDeleteOffline={handleDeleteOffline}
        initialSeek={playerSeek}
        onSeekDone={()=>setPlayerSeek(null)}
        onCardChange={isElectron()?(card)=>{if(card)window.electronAPI.sendCurrentCard(card);}:null}
        autoPlayReq={autoPlayReq} onEnded={()=>queueNextRef.current?.()} queueCount={queue.length} onOpenQueue={()=>setQueueOpen(true)}/>
      {/* Mini player — visible on all screens except player when audio is loaded */}
      {/* Фоновое аудио — элемент */}
      <audio ref={bgAudioRef} style={{display:"none"}}/>
      {/* Мини-плеер для фонового аудио. Скрываем когда открыт bottom-sheet
          (например, SrsPanel) — он должен владеть нижней зоной экрана. */}
      {bgProj&&bgState.dur>0&&!bgMiniClosed&&!bottomSheetOpen&&(
        <MiniPlayer project={bgProj} audio={bgAudioRef.current} cur={bgState.cur} dur={bgState.dur}
          playing={bgState.playing}
          onToggle={()=>{const a=bgAudioRef.current;if(a){if(bgState.playing)a.pause();else a.play().catch(()=>{});}}}
          onSeek={(t)=>{const a=bgAudioRef.current;if(a)a.currentTime=t;}}
          onGoToPlayer={()=>{
            // Возвращаемся к bgProj: переносим bgAudio обратно в Player
            const a=bgAudioRef.current;
            const savedTime=a?.currentTime||0;
            stopBgAudio();
            setActiveProj(bgProj);
            setScreenAndHash("player",bgProj.id);
            setAudioSrc(audioSrc); // восстановится через _loadAudioForProj
            _loadAudioForProj(bgProj).then(()=>{
              // После загрузки — восстанавливаем позицию
              // (небольшая задержка чтобы audio успел загрузиться)
              if(savedTime>0){
                setTimeout(()=>{
                  const pa=document.querySelector("audio");
                  if(pa&&savedTime){pa.currentTime=savedTime;}
                },300);
              }
            });
          }}
          onClose={()=>{stopBgAudio();setBgMiniClosed(true);}}
          queueCount={queue.length} onQueue={()=>setQueueOpen(true)}
          theme={theme}/>
      )}
      {screen!=="player"&&activeProj&&audioSrc&&playerState.dur>0&&!miniClosed&&!bgProj&&!bottomSheetOpen&&(
        <MiniPlayer project={activeProj} audio={playerState.audio} dur={playerState.dur}
          playing={playerState.playing} speed={playerState.speed} vol={playerState.vol}
          onToggle={()=>playerState.toggle?.()}
          onSeek={(t)=>playerState.seek?.(t)}
          onPrevCard={()=>playerState.jumpCard?.("prev")}
          onNextCard={()=>playerState.jumpCard?.("next")}
          onSpeedChange={(s)=>playerState._setSpeed?.(s)}
          onVolChange={(v)=>playerState._setVol?.(v)} muted={!!playerState.muted} onMute={()=>playerState._toggleMute?.()}
          onGoToPlayer={()=>setScreenAndHash("player",activeProj?.id)}
          onClose={()=>{playerState.pause?.();setMiniClosed(true);}}
          skip={playerState.skip||5} clipOn={!!playerState.clipOn} clipLen={playerState.clipLen||5} slowRate={playerState.slowRate||0.6}
          onClip={playerState.toggleClip} onHoldSlow={playerState.holdSlow}
          queueCount={queue.length} onQueue={()=>setQueueOpen(true)}
          theme={theme}/>
      )}
      {NAV_SCREENS.has(screen)&&<PullIndicator {...ptr}/>}
      {NAV_SCREENS.has(screen)&&(
        <TopBar C={THEMES[theme]||THEMES.light} phone={navMode}
          title={screen==="projects"&&activeFolder?activeFolder:NAV_TITLE[screen]}
          onBack={screen==="projects"&&activeFolder?()=>{if(histIdx()>0)window.history.back();else navProjects({folder:null},"replace");}:null}
          search={(!navMode||(screen==="projects"&&!activeFolder))?{value:screen==="projects"?searchQuery:"",placeholder:t("Поиск по карточкам"),
            onChange:(q)=>{if(screen==="projects")setSearchQueryUrl(q);else if(q)setScreenAndHash("projects",null,{search:q});}}:null}
          lang={<LangPicker activeLang={activeLang} onChange={applyActiveLang} langCounts={langCounts} theme={theme} compact={navMode} alignRight/>}
          email={session?.user?.email||""}
          onNew={navMode?(e)=>openNewMenu(e,screen==="projects"?activeFolder:null):null}
          accountItems={[
            {icon:THEME_META[nextTheme(theme,isPro===true)].icon,label:t(THEME_META[nextTheme(theme,isPro===true)].menu),onClick:()=>applyTheme(nextTheme(theme,isPro===true))},
            {icon:"refresh",label:syncing?t("Синхронизация…"):t("Синхронизировать сейчас"),hint:syncResult==="error"?t("Не удалось: {0}",[syncErrorDetail||t("нет связи")]):outboxSize()>0?t("В очереди: {0}",[outboxSize()]):undefined,disabled:syncing,onClick:handleManualSync},
            {icon:"settings",label:t("Аккаунт и настройки"),onClick:()=>setShowAccount(true)},
            isElectron()&&{icon:"layers",label:t("Карточки поверх окон"),hint:t("Alt+Q — показать или скрыть"),onClick:()=>{if(window.electronAPI.toggleOverlay)window.electronAPI.toggleOverlay();else notify(t("Нажмите Alt+Q — карточки появятся поверх всех окон."));}},
            admin&&{icon:"shield",label:t("Админ-панель"),onClick:()=>setAdminOpen(true)},
            "divider",
            {icon:"logout",label:t("Выйти"),danger:true,onClick:handleLogout},
          ]}/>
      )}
      {newMenu&&(
        <PopMenu C={THEMES[theme]||THEMES.light} sheet={newMenu.sheet} at={newMenu.at} width={250}
          title={newMenu.folder?t("Создать в «{0}»",[newMenu.folder]):t("Создать")} onClose={()=>setNewMenu(null)}
          items={[
            {icon:"headphones",label:t("Аудио проект"),hint:t("Карточки по ходу прослушивания"),onClick:()=>startNewAudio(newMenu.folder)},
            {icon:"book",label:t("Текст"),hint:t("Статья, глава, субтитры"),onClick:()=>handleNewTextProject(newMenu.folder||"")},
            {icon:"upload",label:t("Книга из файла"),hint:t("EPUB, PDF или TXT"),onClick:()=>setImportBookOpen(true)},
            !newMenu.folder&&{icon:"folder",label:t("Папка"),onClick:()=>{if(screen!=="projects")setScreenAndHash("projects");setNewFolderReq(Date.now());}},
          ]}/>
      )}
      {screen==="review"&&<ReviewScreen theme={theme} navMode={navMode} projects={activeLang?projects.filter(p=>(p.src_lang||'').toLowerCase()===activeLang):projects} activeLang={activeLang}
        onQuiz={openQuiz} onGames={openGames}/>}
      {screen==="audio"&&<AudioScreen theme={theme} navMode={navMode} projects={projects} activeLang={activeLang}
        onOpen={handleOpen} onPlayHere={handlePlayHere} playingId={playerState.playing&&!bgProj?activeProj?.id:null}
        onNew={()=>startNewAudio(null)} onOpenQueue={()=>setQueueOpen(true)} queueCount={queue.length}/>}
      {screen==="reading"&&<ReadingScreen theme={theme} navMode={navMode} textProjects={textProjects} activeLang={activeLang}
        onOpen={handleOpenTextProject} onNew={()=>handleNewTextProject()} onImportBook={()=>setImportBookOpen(true)}/>}
      {screen==="quiz"&&<QuizScreen allProjects={activeLang?projects.filter(p=>(p.src_lang||'').toLowerCase()===activeLang):projects} projectId={quizProjId} autoSrs={quizAutoSrs} theme={theme} onTheme={applyTheme} onBack={()=>{try{sessionStorage.removeItem("ac_quiz_nav");}catch{}goBack("review");}} onGames={openGames} onSaveProject={handleSaveProject} onOpenFsrsSettings={()=>setShowFsrsSettings(true)}/>}
      {screen==="games"&&<Suspense fallback={null}><GamesScreen key={gamesLeech?"l":"a"} projects={activeLang?projects.filter(p=>(p.src_lang||'').toLowerCase()===activeLang):projects} leeches={gamesLeech} navMode={navMode} onBack={()=>goBack("review")}/></Suspense>}
      {screen==="stats"&&<StatsScreen theme={theme} navMode={navMode} activeLang={activeLang} onActiveLang={applyActiveLang} onClearStats={clearStats.clear}
        projectLangs={[
          ...projects.map(p=>(p.src_lang||'').toLowerCase()),
          ...textProjects.map(p=>(p.src_lang||'').toLowerCase()),
        ].filter(Boolean)}/>}
      {screen==="projects"&&<ProjectsScreen slotWords={slotWords} userName={userName} email={session?.user?.email||""} navMode={navMode} newFolderReq={newFolderReq} projects={projects} extraFolders={extraFolders} loading={projLoading}
        onNew={startNewAudio} onNewMenu={openNewMenu}
        onOpen={handleOpen} onDelete={handleDelete} onRename={handleRename} onMoveToFolder={handleMoveToFolder}
        queue={queue} onQueueAdd={(p)=>addToQueue(p.id)} onQueueNext={(p)=>playNextInQueue(p.id)} onQueueRemove={(p)=>removeFromQueue(p.id)} onOpenQueue={()=>setQueueOpen(true)}
        onPlayHere={handlePlayHere} playingId={playerState.playing&&!bgProj?activeProj?.id:null}
        announcement={announcement} onDismissAnnouncement={dismissAnnouncement}
        onCreateFolder={handleCreateFolder} onDeleteFolder={handleDeleteFolder}
        syncResult={syncResult} syncErrorDetail={syncErrorDetail}
        onQuiz={openQuiz} onGames={openGames}
        textProjects={textProjects}
        onOpenTextProject={handleOpenTextProject}
        onDeleteTextProject={handleDeleteTextProject}
        onRenameTextProject={handleRenameTextProject}
        onMoveTextToFolder={handleMoveTextToFolder}
        onNewTextProject={handleNewTextProject}
        onImportBook={()=>setImportBookOpen(true)}
        onLinkTextToAudio={handleLinkTextToAudio}
        downloadStatus={dlStatus} onDownload={handleDownload} onDeleteOffline={handleDeleteOffline}
        onBulkDownload={handleBulkDownload}
        activeLang={activeLang}
        activeFolder={activeFolder} onActiveFolder={setActiveFolderUrl}
        searchQuery={searchQuery} onSearchQuery={setSearchQueryUrl}
        onAssignLang={handleAssignLang}
        theme={theme}/>}
      {navMode&&NAV_SCREENS.has(screen)&&(
        <BottomNav pal={THEMES[theme]||THEMES.light} active={NAV_ACTIVE[screen]} due={due}
          onHome={goHome} onAudio={()=>setScreenAndHash("audio")} onReview={()=>setScreenAndHash("review")} onRead={()=>setScreenAndHash("reading")} onStats={()=>setScreenAndHash("stats")}/>
      )}
      {!navMode&&NAV_SCREENS.has(screen)&&(
        <SideNav pal={THEMES[theme]||THEMES.light} compact={sideCompact} active={NAV_ACTIVE[screen]} due={due}
          onHome={goHome} onAudio={()=>setScreenAndHash("audio")} onReview={()=>setScreenAndHash("review")} onRead={()=>setScreenAndHash("reading")} onStats={()=>setScreenAndHash("stats")}
          onNew={(e)=>openNewMenu(e,screen==="projects"?activeFolder:null)}
          onSync={handleManualSync} syncing={syncing} syncResult={syncResult} syncErrorDetail={syncErrorDetail} words={slotWords}
          todayCards={langCards} onStartReview={()=>openQuiz(null,true)}/>
      )}
      {screen==="upload"&&<UploadScreen project={activeProj} onUpload={handleUpload} uploading={uploading} compressProgress={compressProgress} onBack={()=>goBack("projects")} theme={theme} onTheme={applyTheme}/>}
      {screen==="ui"&&<Suspense fallback={null}><UiGallery/></Suspense>}
    </>
    </WordLearningProvider>
    </ThemeContext.Provider>
  );
}
